export const SCOPE_RESOURCE_TO_CATEGORY: Record<string, string> = {
  ext_user: 'Contacts',
  room_session: 'Meetings',
  room: 'Meetings',
  room_event: 'Meetings',
  room_record: 'Meetings',
  airesult: 'Results',
  aifeature: 'Results',
  ai_agent: 'Results',
  ai_agent_result: 'Results',
  integration: 'Integrations',
  task: 'Tasks',
  meeting_template: 'Meeting Templates',
  meeting_note: 'Meeting Notes',
  scorecard: 'Scorecards',
  scorecardresult: 'Scorecards',
  webhook: 'Webhooks',
  user: 'Users',
  insights: 'Analytics',
  file: 'Transcripts',
  business_event: 'Client Signals',
}

// Curated display order for scope categories. Categories not listed here are
// appended after these (in encounter order), with 'Other' always kept last.
export const SCOPE_CATEGORY_ORDER: string[] = [
  'Users',
  'Contacts',
  'Meetings',
  'Meeting Templates',
  'Results',
  'Tasks',
  'Transcripts',
  'Integrations',
  'Client Signals',
]

// User-facing descriptions for OAuth2 scopes, keyed by scope id. They override
// the terser descriptions returned by the IAM permissions endpoint; callers fall
// back to the API description for any scope not listed here.
export const SCOPE_DESCRIPTIONS: Record<string, string> = {
  // Contacts
  'ext_user:list': 'List your Zocks contacts',
  'ext_user:read': 'View full contact profiles',
  'ext_user:create': 'Create new contacts',
  // Users
  'user:list': 'List team members',
  'user:read': 'View team member profiles',
  // Meetings
  'room:read': 'View meeting room details',
  'room_session:list': 'List meetings',
  'room_session:read': 'View meeting details and metadata',
  // Results
  'airesult:list': 'List AI results for a meeting',
  'airesult:read': 'View full AI result content',
  'ai_agent_result:list': 'List AI agent results',
  'ai_agent_result:read': 'View full AI agent output',
  'ai_agent:read': 'View AI agent details',
  'aifeature:read': 'View AI feature details',
  'aifeature:list': 'List AI features',
  // Meeting templates
  'meeting_template:list': 'List meeting templates',
  'meeting_template:read': 'View meeting template details',
  // Tasks
  'task:list': 'List meeting tasks',
  'task:read': 'View task details',
  // Transcripts
  'file:download': 'Download transcripts',
  // Client Signals
  'business_event:list': 'List Client Signals',
  'business_event:read': 'View Client Signal details',
}

export const getScopeDescription = (scopeId: string, fallback: string): string => SCOPE_DESCRIPTIONS[scopeId] ?? fallback

// Scopes whose SCOPE_DESCRIPTIONS entry also replaces the IAM description on the
// consent screen; every other scope keeps the IAM description there.
export const CONSENT_DESCRIPTION_OVERRIDE_SCOPE_IDS: ReadonlySet<string> = new Set(['business_event:list', 'business_event:read'])

export type ScopeAccessType = 'read' | 'write' | 'admin'

const READ_ACTIONS = new Set(['read', 'list', 'get', 'download', 'download_attachment', 'sample'])

export const getScopeAccessType = (scopeId: string): ScopeAccessType => {
  const action = scopeId.split(':')[1] ?? ''
  if (action === 'admin' || action.startsWith('admin_')) {
    return 'admin'
  }
  if (READ_ACTIONS.has(action)) {
    return 'read'
  }
  return 'write'
}

export const getScopeCategory = (scopeId: string): string => {
  const resource = scopeId.split(':')[0]
  return SCOPE_RESOURCE_TO_CATEGORY[resource] ?? 'Other'
}

export const groupByScopeCategory = <T>(items: T[], getItemCategory: (item: T) => string): Map<string, T[]> => {
  const grouped = items.reduce((acc, item) => {
    const category = getItemCategory(item)
    const list = acc.get(category) ?? []
    acc.set(category, [...list, item])
    return acc
  }, new Map<string, T[]>())

  const rank = (category: string) => {
    const index = SCOPE_CATEGORY_ORDER.indexOf(category)
    if (index !== -1) {
      return index
    }
    // Unlisted categories sort after the curated ones, but 'Other' always last.
    return category === 'Other' ? Number.MAX_SAFE_INTEGER : SCOPE_CATEGORY_ORDER.length
  }

  return new Map([...grouped.entries()].sort(([a], [b]) => rank(a) - rank(b)))
}

export const groupPermissionsByScopeCategory = (permissions: { id: string; description: string }[]): Map<string, { id: string; description: string }[]> =>
  groupByScopeCategory(permissions, (p) => getScopeCategory(p.id))
